import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOwnerMode } from './OwnerModeContext';
import MortgageBoard from './board/MortgageBoard';
import REBoard from './board/REBoard';
import OpportunityEmblem from './board/OpportunityEmblem';
import PageContainer from './kit/PageContainer';

// One entry per board flavor. A tenant sees the boards it is licensed for
// (hasProduct); a dual-licensed tenant gets a segmented switcher and one
// board at a time — never a merged view, the stage sets differ.
const BOARDS = [
  { key: 'mortgage', label: 'Mortgage', Board: MortgageBoard, subtitle: 'opportunities' },
  { key: 'real_estate', label: 'Real Estate', Board: REBoard, subtitle: 'buyer and seller opportunities' },
];

// Last-selected board, per browser. localStorage can throw (private mode,
// disabled storage) — treat any failure as "no preference".
const STORAGE_KEY = 'pipeline.board';
const readBoard = () => { try { return localStorage.getItem(STORAGE_KEY); } catch { return null; } };
const writeBoard = (k) => { try { localStorage.setItem(STORAGE_KEY, k); } catch { /* ignore */ } };

export default function PipelinePage() {
  const { hasProduct } = useOwnerMode();
  const available = useMemo(() => BOARDS.filter(b => hasProduct(b.key)), [hasProduct]);
  const [chosen, setChosen] = useState(readBoard);
  const [initialOpenId, setInitialOpenId] = useState(null);
  const [newForContactId, setNewForContactId] = useState(null);
  // A manual switch remounts the board (keyed), so drop any pending deep link first.
  const select = useCallback((k) => { setChosen(k); writeBoard(k); setInitialOpenId(null); setNewForContactId(null); }, []);

  // Deep link (`?board=real_estate&open=<opportunity_id>`, e.g. from a
  // contact's opportunity list): applied once it names a licensed board,
  // then dropped from the URL so the remembered choice stays the rule.
  // `available` can start mortgage-only until the tenant loads, so an
  // unmatched value is left alone, not stripped.
  const [params, setParams] = useSearchParams();
  const wanted = params.get('board');
  const wantedOpen = params.get('open');
  const wantedNewFor = params.get('new_for');
  useEffect(() => {
    if (!wanted || !available.some(b => b.key === wanted)) return;
    select(wanted);
    if (wantedOpen) setInitialOpenId(wantedOpen);
    // `new_for=<contact_id>` (the profile's "New opportunity" link): create dialog, contact picked.
    if (wantedNewFor && wanted === 'mortgage') setNewForContactId(wantedNewFor);
    setParams({}, { replace: true });
  }, [wanted, wantedOpen, wantedNewFor, available, select, setParams]);

  // Remembered choice wins if it's still licensed; else the first licensed
  // board (mortgage comes first, so it's the default).
  const active = available.find(b => b.key === chosen) || available[0];

  if (!active) return null;
  const { Board } = active;

  const switcher = available.length > 1 && (
    <div className="inline-flex rounded-full border border-slate-200 overflow-hidden p-0.5 bg-slate-50" role="group" aria-label="Pipeline board" data-testid="pipeline-board-switcher">
      {available.map(b => (
        <button key={b.key} type="button" onClick={() => select(b.key)} data-testid={`pipeline-board-${b.key}`}
          aria-pressed={active.key === b.key}
          className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold rounded-full transition-colors ${active.key === b.key ? 'bg-brand-navy text-white' : 'text-slate-500'}`}>
          <OpportunityEmblem product={b.key} size={14} decorative />
          {b.label}
        </button>
      ))}
    </div>
  );

  return (
    <PageContainer
      title="Pipeline"
      subtitle={<>Drag {active.subtitle} between stages. Tip: everything auto-saves.</>}
      actions={switcher}
      titleProps={{ 'data-testid': 'pipeline-title' }}
      subtitleProps={{ 'data-testid': 'pipeline-subtitle' }}
    >
      <Board key={active.key} initialOpenId={initialOpenId} newForContactId={newForContactId} />
    </PageContainer>
  );
}
